import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { Form, Input, Select, DatePicker, Row, Col } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import styled from "styled-components";
import { usePost } from "../../hooks/usePost";
import { useToast } from "../../store/ToastProvider";
import { MilestoneKey } from "../../enums/Status";
import { MILESTONE_DESCRIPTIONS } from "../CompanyProfile/constants";
import { getSdgIcon } from "../../utils/formattingUtils";
import { Icon } from "../../components/visuals/styledComponents";
import { MESSAGES } from "../../messages";
import { greyText, ESCALATION_COLORS } from "../../styles";
import {
  EscalationKey,
  EscalationParent,
  formatEscalationCode,
} from "../../enums/Escalations";
import { ESCALATION_LABELS } from "../../utils/escalationLabels";
import { InlineInteractionPrefill } from "../../components/common/InlinePrefill";
import { AIPrefillInteractionResponse } from "../../types/compassAITypes";
import { EngagementCategory } from "../../models/stewardshipModels";

const { TextArea } = Input;

// Styled Components
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledFormWrapper = styled.div`
  width: 100%;

  .ant-select-selector,
  .ant-picker,
  .ant-input,
  .ant-input-textarea {
    width: 100%;
  }

  .ant-form-item-label > label {
    color: ${greyText} !important;
  }
`;

const EscalationBadge = styled.span<{ bgColor: string; isSelected?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ isSelected }): string => (isSelected ? "1px 6px" : "2px 8px")};
  border-radius: ${({ isSelected }): string => (isSelected ? "10px" : "12px")};
  background-color: ${({ bgColor }): string => bgColor};
  font-size: ${({ isSelected }): string => (isSelected ? "11px" : "12px")};
  font-weight: 500;
  white-space: nowrap;
`;

// Form Field Names
enum FormFieldName {
  ENGAGEMENT_RECORD_ID = "engagement_record_id",
  ENGAGEMENT_NAME = "engagement_name",
  UPDATE_DATE = "update_date",
  ENGAGEMENT_MILESTONE = "engagement_milestone",
  LATEST_INTERACTION_ACTIVITY = "latest_interaction_activity",
  LATEST_INTERACTION_MOMENTUM = "latest_interaction_momentum",
  ESCALATIONS = "escalations",
  SDGS = "sdgs",
}

// SDG Configuration
const SDG_NAMES = MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.SDG_NAMES;
const SDG_DATA = Object.entries(SDG_NAMES).map(([id, name]) => ({
  id: Number(id),
  name,
}));

// Escalation Configuration - grouped by parent
const ESCALATION_DATA = [
  // E1 - Raising Concerns
  {
    key: EscalationKey.E1_1,
    label: ESCALATION_LABELS.E1_1,
    parent: EscalationParent.E1,
    level: 1,
  },
  {
    key: EscalationKey.E1_2,
    label: ESCALATION_LABELS.E1_2,
    parent: EscalationParent.E1,
    level: 1,
  },
  {
    key: EscalationKey.E1_3,
    label: ESCALATION_LABELS.E1_3,
    parent: EscalationParent.E1,
    level: 1,
  },
  {
    key: EscalationKey.E1_4,
    label: ESCALATION_LABELS.E1_4,
    parent: EscalationParent.E1,
    level: 1,
  },
  // E2 - Collaborative Measure
  {
    key: EscalationKey.E2_1,
    label: ESCALATION_LABELS.E2_1,
    parent: EscalationParent.E2,
    level: 2,
  },
  {
    key: EscalationKey.E2_2,
    label: ESCALATION_LABELS.E2_2,
    parent: EscalationParent.E2,
    level: 2,
  },
  {
    key: EscalationKey.E2_3,
    label: ESCALATION_LABELS.E2_3,
    parent: EscalationParent.E2,
    level: 2,
  },
  {
    key: EscalationKey.E2_4,
    label: ESCALATION_LABELS.E2_4,
    parent: EscalationParent.E2,
    level: 2,
  },
  {
    key: EscalationKey.E2_5,
    label: ESCALATION_LABELS.E2_5,
    parent: EscalationParent.E2,
    level: 2,
  },
  {
    key: EscalationKey.E2_6,
    label: ESCALATION_LABELS.E2_6,
    parent: EscalationParent.E2,
    level: 2,
  },
  // E3 - Shareholder Advocacy
  {
    key: EscalationKey.E3_1,
    label: ESCALATION_LABELS.E3_1,
    parent: EscalationParent.E3,
    level: 3,
  },
  {
    key: EscalationKey.E3_2,
    label: ESCALATION_LABELS.E3_2,
    parent: EscalationParent.E3,
    level: 3,
  },
  {
    key: EscalationKey.E3_3,
    label: ESCALATION_LABELS.E3_3,
    parent: EscalationParent.E3,
    level: 3,
  },
  {
    key: EscalationKey.E3_4,
    label: ESCALATION_LABELS.E3_4,
    parent: EscalationParent.E3,
    level: 3,
  },
  {
    key: EscalationKey.E3_5,
    label: ESCALATION_LABELS.E3_5,
    parent: EscalationParent.E3,
    level: 3,
  },
  {
    key: EscalationKey.E3_6,
    label: ESCALATION_LABELS.E3_6,
    parent: EscalationParent.E3,
    level: 3,
  },
  {
    key: EscalationKey.E3_7,
    label: ESCALATION_LABELS.E3_7,
    parent: EscalationParent.E3,
    level: 3,
  },
  {
    key: EscalationKey.E3_8,
    label: ESCALATION_LABELS.E3_8,
    parent: EscalationParent.E3,
    level: 3,
  },
  // E4 - Capital Allocation Decisions
  {
    key: EscalationKey.E4_1,
    label: ESCALATION_LABELS.E4_1,
    parent: EscalationParent.E4,
    level: 4,
  },
  {
    key: EscalationKey.E4_2,
    label: ESCALATION_LABELS.E4_2,
    parent: EscalationParent.E4,
    level: 4,
  },
  {
    key: EscalationKey.E4_3,
    label: ESCALATION_LABELS.E4_3,
    parent: EscalationParent.E4,
    level: 4,
  },
  // E5 - Levers of Last Resort
  {
    key: EscalationKey.E5_1,
    label: ESCALATION_LABELS.E5_1,
    parent: EscalationParent.E5,
    level: 5,
  },
  {
    key: EscalationKey.E5_2,
    label: ESCALATION_LABELS.E5_2,
    parent: EscalationParent.E5,
    level: 5,
  },
];

// Milestone Options (M1-M4 for objectives)
const MILESTONE_OPTIONS = [
  MilestoneKey.M1,
  MilestoneKey.M2,
  MilestoneKey.M3,
  MilestoneKey.M4,
].map((key) => ({
  value: key,
  label: `${key}: ${MILESTONE_DESCRIPTIONS[key]}`,
}));

// CI-only option for Corporate Issues
const CI_MILESTONE_OPTION = [
  {
    value: MilestoneKey.CI,
    label: `${MilestoneKey.CI}: ${MILESTONE_DESCRIPTIONS[MilestoneKey.CI]}`,
  },
];

// Momentum Options
const MOMENTUM_OPTIONS = [
  { value: "Positive", label: "Positive" },
  { value: "Neutral", label: "Neutral" },
  { value: "Negative", label: "Negative" },
];

// Types
interface EngagementUpdateFormValues {
  [FormFieldName.ENGAGEMENT_RECORD_ID]: string;
  [FormFieldName.ENGAGEMENT_NAME]: string;
  [FormFieldName.UPDATE_DATE]: Dayjs;
  [FormFieldName.ENGAGEMENT_MILESTONE]: MilestoneKey;
  [FormFieldName.LATEST_INTERACTION_ACTIVITY]: string;
  [FormFieldName.LATEST_INTERACTION_MOMENTUM]?: string;
  [FormFieldName.ESCALATIONS]?: string[]; // Array of escalation keys like "E1_1", "E2_3"
  [FormFieldName.SDGS]?: number[]; // Array of SDG IDs (1-17)
}

interface AddEngagementUpdateRequest {
  engagement_record_id: string;
  engagement_name: string;
  engagement_milestone: string;
  update_date: string;
  latest_interaction_activity: string;
  latest_interaction_momentum?: string;
  // E Pillars
  e1_1?: boolean;
  e1_2?: boolean;
  e1_3?: boolean;
  e1_4?: boolean;
  e2_1?: boolean;
  e2_2?: boolean;
  e2_3?: boolean;
  e2_4?: boolean;
  e2_5?: boolean;
  e2_6?: boolean;
  e3_1?: boolean;
  e3_2?: boolean;
  e3_3?: boolean;
  e3_4?: boolean;
  e3_5?: boolean;
  e3_6?: boolean;
  e3_7?: boolean;
  e3_8?: boolean;
  e4_1?: boolean;
  e4_2?: boolean;
  e4_3?: boolean;
  e5_1?: boolean;
  e5_2?: boolean;
  // SDGs
  sdg_1?: boolean;
  sdg_2?: boolean;
  sdg_3?: boolean;
  sdg_4?: boolean;
  sdg_5?: boolean;
  sdg_6?: boolean;
  sdg_7?: boolean;
  sdg_8?: boolean;
  sdg_9?: boolean;
  sdg_10?: boolean;
  sdg_11?: boolean;
  sdg_12?: boolean;
  sdg_13?: boolean;
  sdg_14?: boolean;
  sdg_15?: boolean;
  sdg_16?: boolean;
  sdg_17?: boolean;
}

interface AddEngagementUpdateResponse {
  data: {
    update_id: string;
  };
}

export interface EngagementUpdateFormRef {
  submit: () => void;
  isLoading: boolean;
}

interface EngagementUpdateFormProps {
  engagementRecordId: string;
  engagementName: string;
  engagementCategory?: EngagementCategory;
  onSubmitStart?: () => void;
  onSuccess?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

// Helper to render SDG option with icon
const SDGOption = (id: number, name: string): JSX.Element => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <Icon
      src={getSdgIcon(id)}
      alt={`SDG ${id}`}
      style={{ width: "24px", height: "24px" }}
    />
    <span>
      SDG {id}: {name}
    </span>
  </div>
);

// Helper to render escalation option with colored badge
const EscalationOption = (
  key: string,
  label: string,
  level: number,
): JSX.Element => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <EscalationBadge bgColor={ESCALATION_COLORS[level]}>
      {formatEscalationCode(key)}
    </EscalationBadge>
    <span>{label}</span>
  </div>
);

export const EngagementUpdateForm = forwardRef<
  EngagementUpdateFormRef,
  EngagementUpdateFormProps
>(
  (
    {
      engagementRecordId,
      engagementCategory,
      onSubmitStart,
      onSuccess,
      onLoadingChange,
    },
    ref,
  ) => {
    const isCorporateIssue = engagementCategory === EngagementCategory.Issue;
    const [form] = Form.useForm<EngagementUpdateFormValues>();
    const { addToast } = useToast();

    const {
      sendData: addEngagementUpdate,
      isLoading: isSubmitting,
      error: submitError,
    } = usePost<AddEngagementUpdateResponse, AddEngagementUpdateRequest>();

    // Expose form methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        submit: (): void => form.submit(),
        isLoading: isSubmitting,
      }),
      [form, isSubmitting],
    );

    // Notify parent of loading state changes
    useEffect(() => {
      onLoadingChange?.(isSubmitting);
    }, [isSubmitting, onLoadingChange]);

    useEffect(() => {
      if (submitError) {
        addToast(
          MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ENGAGEMENT_UPDATE.CREATE_ERROR,
          "error",
        );
      }
    }, [submitError]);

    // =========================================================================
    // AI Pre-fill handler
    // =========================================================================
    const handlePrefillSuccess = useCallback(
      (result: AIPrefillInteractionResponse): void => {
        const { extracted_fields, fields_found } = result;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const patch: Record<string, any> = {};
        let fieldCount = 0;

        // Description (interaction_title from API, maps to engagement_name form field)
        if (
          fields_found.includes("interaction_title") &&
          extracted_fields.interaction_title
        ) {
          patch[FormFieldName.ENGAGEMENT_NAME] =
            extracted_fields.interaction_title;
          fieldCount++;
        }

        // Update Date
        if (
          fields_found.includes("update_date") &&
          extracted_fields.update_date
        ) {
          const parsed = dayjs(extracted_fields.update_date);
          if (parsed.isValid()) {
            patch[FormFieldName.UPDATE_DATE] = parsed;
            fieldCount++;
          }
        }

        // Milestone (skip for Corporate Issues — always CI)
        if (
          !isCorporateIssue &&
          fields_found.includes("engagement_milestone") &&
          extracted_fields.engagement_milestone
        ) {
          const milestone =
            extracted_fields.engagement_milestone.toUpperCase() as MilestoneKey;
          if (
            [
              MilestoneKey.M1,
              MilestoneKey.M2,
              MilestoneKey.M3,
              MilestoneKey.M4,
            ].includes(milestone)
          ) {
            patch[FormFieldName.ENGAGEMENT_MILESTONE] = milestone;
            fieldCount++;
          }
        }

        // Interaction Activity (Details)
        if (
          fields_found.includes("latest_interaction_activity") &&
          extracted_fields.latest_interaction_activity
        ) {
          patch[FormFieldName.LATEST_INTERACTION_ACTIVITY] =
            extracted_fields.latest_interaction_activity;
          fieldCount++;
        }

        // Momentum
        if (
          fields_found.includes("latest_interaction_momentum") &&
          extracted_fields.latest_interaction_momentum
        ) {
          const validMomentum = ["Positive", "Neutral", "Negative"];
          const momentum = extracted_fields.latest_interaction_momentum;
          // Case-insensitive match
          const matched = validMomentum.find(
            (v) => v.toLowerCase() === momentum.toLowerCase(),
          );
          if (matched) {
            patch[FormFieldName.LATEST_INTERACTION_MOMENTUM] = matched;
            fieldCount++;
          }
        }

        // Escalations — convert boolean fields to array of EscalationKey
        const escalationKeys: EscalationKey[] = [];
        const allEscalationKeys = Object.values(EscalationKey);
        for (const key of allEscalationKeys) {
          const apiField = key.toLowerCase() as keyof typeof extracted_fields;
          if (extracted_fields[apiField] === true) {
            escalationKeys.push(key);
          }
        }
        if (escalationKeys.length > 0) {
          patch[FormFieldName.ESCALATIONS] = escalationKeys;
          fieldCount++;
        }

        // SDGs — convert boolean fields to array of numbers
        const sdgIds: number[] = [];
        for (let i = 1; i <= 17; i++) {
          const apiField = `sdg_${i}` as keyof typeof extracted_fields;
          if (extracted_fields[apiField] === true) {
            sdgIds.push(i);
          }
        }
        if (sdgIds.length > 0) {
          patch[FormFieldName.SDGS] = sdgIds;
          fieldCount++;
        }

        // Apply to form
        if (fieldCount > 0) {
          form.setFieldsValue(patch);
          addToast(
            `Pre-filled ${fieldCount} field${fieldCount > 1 ? "s" : ""}`,
            "success",
          );
        }
      },
      [form, addToast, isCorporateIssue],
    );

    // Build SDG options for multi-select
    const sdgOptions = useMemo(() => {
      return SDG_DATA.map((sdg) => ({
        key: sdg.id.toString(),
        value: sdg.id,
        label: SDGOption(sdg.id, sdg.name),
      }));
    }, []);

    // Build escalation options for multi-select with option groups
    const escalationOptions = useMemo(() => {
      // Group escalations by parent for better organization
      const groups = [
        {
          label: EscalationParent.E1,
          options: ESCALATION_DATA.filter((e) => e.level === 1),
        },
        {
          label: EscalationParent.E2,
          options: ESCALATION_DATA.filter((e) => e.level === 2),
        },
        {
          label: EscalationParent.E3,
          options: ESCALATION_DATA.filter((e) => e.level === 3),
        },
        {
          label: EscalationParent.E4,
          options: ESCALATION_DATA.filter((e) => e.level === 4),
        },
        {
          label: EscalationParent.E5,
          options: ESCALATION_DATA.filter((e) => e.level === 5),
        },
      ];

      return groups.map((group) => ({
        label: <span style={{ fontWeight: 600 }}>{group.label}</span>,
        options: group.options.map((esc) => ({
          key: esc.key,
          value: esc.key,
          label: EscalationOption(esc.key, esc.label, esc.level),
        })),
      }));
    }, []);

    const onFinish = async (
      values: EngagementUpdateFormValues,
    ): Promise<void> => {
      // Notify parent that submit has started (to close modal immediately)
      onSubmitStart?.();

      // Convert selected escalations array to individual boolean fields
      const escalationBooleans: Record<string, boolean> = {};
      ESCALATION_DATA.forEach((esc) => {
        const fieldKey = esc.key.toLowerCase();
        escalationBooleans[fieldKey] =
          values.escalations?.includes(esc.key) || false;
      });

      // Convert selected SDGs array to individual boolean fields
      const sdgBooleans: Record<string, boolean> = {};
      for (let i = 1; i <= 17; i++) {
        sdgBooleans[`sdg_${i}`] = values.sdgs?.includes(i) || false;
      }

      const requestPayload: AddEngagementUpdateRequest = {
        engagement_record_id: engagementRecordId,
        engagement_name: values.engagement_name,
        engagement_milestone: values.engagement_milestone,
        update_date: values.update_date?.format("YYYY-MM-DD"),
        latest_interaction_activity: values.latest_interaction_activity,
        latest_interaction_momentum: values.latest_interaction_momentum,
        // Spread escalation booleans
        ...escalationBooleans,
        // Spread SDG booleans
        ...sdgBooleans,
      } as AddEngagementUpdateRequest;

      const result = await addEngagementUpdate(
        "ADD_ENGAGEMENT_UPDATE",
        requestPayload,
      );

      if (result) {
        addToast(
          MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ENGAGEMENT_UPDATE
            .CREATE_SUCCESS,
          "success",
        );
        form.resetFields();
        onSuccess?.();
      }
    };

    // Default form values
    const initialValues: Partial<EngagementUpdateFormValues> = {
      engagement_milestone: isCorporateIssue
        ? MilestoneKey.CI
        : MilestoneKey.M1,
    };

    return (
      <FormContainer>
        <StyledFormWrapper>
          <Form<EngagementUpdateFormValues>
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={initialValues}
            size="large"
          >
            {/* Hidden field for engagement_record_id */}
            <Form.Item name={FormFieldName.ENGAGEMENT_RECORD_ID} hidden>
              <Input />
            </Form.Item>

            {/* Engagement Name - maps to timeline description */}
            <Form.Item
              name={FormFieldName.ENGAGEMENT_NAME}
              label="Description"
              rules={[
                { required: true, message: "Please enter a description" },
                {
                  max: 512,
                  message: "Description must be 512 characters or less",
                },
              ]}
            >
              <Input placeholder="Brief summary of the interaction (e.g., Scope 3 Discussion with CFO)" />
            </Form.Item>

            {/* AI Pre-fill */}
            <InlineInteractionPrefill
              engagementRecordId={engagementRecordId || null}
              onSuccess={handlePrefillSuccess}
            />

            <Row gutter={16}>
              {/* Update Date */}
              <Col xs={24} sm={12}>
                <Form.Item
                  name={FormFieldName.UPDATE_DATE}
                  label="Update Date"
                  rules={[
                    { required: true, message: "Please select an update date" },
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Engagement Milestone */}
              <Col xs={24} sm={12}>
                <Form.Item
                  name={FormFieldName.ENGAGEMENT_MILESTONE}
                  label="Engagement Milestone"
                  rules={[
                    { required: true, message: "Please select a milestone" },
                  ]}
                >
                  <Select
                    options={
                      isCorporateIssue ? CI_MILESTONE_OPTION : MILESTONE_OPTIONS
                    }
                    placeholder="Select milestone"
                    disabled={isCorporateIssue}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Latest Interaction Activity - maps to timeline details */}
            <Form.Item
              name={FormFieldName.LATEST_INTERACTION_ACTIVITY}
              label="Details"
              rules={[
                { required: true, message: "Please enter interaction details" },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Details about the interaction..."
              />
            </Form.Item>

            {/* Latest Interaction Momentum */}
            <Form.Item
              name={FormFieldName.LATEST_INTERACTION_MOMENTUM}
              label="Latest Interaction Momentum"
            >
              <Select
                options={MOMENTUM_OPTIONS}
                placeholder="Select momentum"
                allowClear
              />
            </Form.Item>

            {/* Escalations Multi-Select */}
            <Form.Item
              name={FormFieldName.ESCALATIONS}
              label="Escalation Measures"
            >
              <Select
                mode="multiple"
                placeholder="Select escalation measures"
                options={escalationOptions}
                allowClear
                showSearch
                optionLabelProp="label"
                tagRender={({ value, closable, onClose }): JSX.Element => {
                  if (!value) return <span />;
                  const esc = ESCALATION_DATA.find((e) => e.key === value);
                  return (
                    <span
                      className="ant-select-selection-item"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <span
                        className="ant-select-selection-item-content"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <EscalationBadge
                          bgColor={ESCALATION_COLORS[esc?.level || 1]}
                          isSelected
                        >
                          {formatEscalationCode(String(value))}
                        </EscalationBadge>
                        <span>{esc?.label}</span>
                      </span>
                      {closable && (
                        <span
                          className="ant-select-selection-item-remove"
                          onClick={onClose}
                          style={{ marginLeft: 4 }}
                        >
                          ×
                        </span>
                      )}
                    </span>
                  );
                }}
                filterOption={(input, option): boolean => {
                  // Option is the inner option from the group, not the group itself
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const optionValue = (option as any)?.value as
                    | EscalationKey
                    | undefined;
                  const esc = ESCALATION_DATA.find(
                    (e) => e.key === optionValue,
                  );
                  return esc
                    ? esc.label.toLowerCase().includes(input.toLowerCase()) ||
                        esc.key.toLowerCase().includes(input.toLowerCase())
                    : false;
                }}
              />
            </Form.Item>

            {/* SDGs Multi-Select */}
            <Form.Item
              name={FormFieldName.SDGS}
              label="Sustainable Development Goals (SDGs)"
            >
              <Select
                mode="multiple"
                placeholder="Select relevant SDGs"
                options={sdgOptions}
                allowClear
                showSearch
                optionLabelProp="label"
                filterOption={(input, option): boolean => {
                  const sdg = SDG_DATA.find((s) => s.id === option?.value);
                  return sdg
                    ? sdg.name.toLowerCase().includes(input.toLowerCase()) ||
                        sdg.id.toString().includes(input)
                    : false;
                }}
              />
            </Form.Item>
          </Form>
        </StyledFormWrapper>
      </FormContainer>
    );
  },
);

EngagementUpdateForm.displayName = "EngagementUpdateForm";
